/**
 * Project PIP-176 seed rings into unit-viewBox SVG paths for row hoodmarks
 * (WEB-787 / DPL-21). Same converter the API-1657 boundary hook uses, so a
 * later denser payload can replace these without a second projection.
 */
import type { GeoJsonBbox } from '@loci/validation';

import { CLT_NEIGHBORHOOD_SLUGS, type CltNeighborhoodSlug } from '@/lib/neighborhoods/clt-slugs';
import { CLT_SEED_RINGS, type LonLatRing } from '@/lib/neighborhoods/clt-seed-rings';
import { boundaryToFootprintPath } from '@/lib/neighborhoods/footprintPath';

function bboxFromRing(ring: LonLatRing): GeoJsonBbox | null {
  let minLon = Infinity;
  let minLat = Infinity;
  let maxLon = -Infinity;
  let maxLat = -Infinity;
  for (const [lon, lat] of ring) {
    minLon = Math.min(minLon, lon);
    minLat = Math.min(minLat, lat);
    maxLon = Math.max(maxLon, lon);
    maxLat = Math.max(maxLat, lat);
  }
  if (!(maxLon > minLon) || !(maxLat > minLat)) return null;
  return [minLon, minLat, maxLon, maxLat];
}

function projectSeedRing(ring: LonLatRing): string | null {
  const bbox = bboxFromRing(ring);
  return boundaryToFootprintPath(
    { type: 'Polygon', coordinates: [ring.map(([lon, lat]) => [lon, lat])] },
    bbox,
  );
}

const PATHS: ReadonlyMap<CltNeighborhoodSlug, string> = (() => {
  const out = new Map<CltNeighborhoodSlug, string>();
  for (const slug of CLT_NEIGHBORHOOD_SLUGS) {
    const path = projectSeedRing(CLT_SEED_RINGS[slug]);
    if (path) out.set(slug, path);
  }
  return out;
})();

/** Own-bbox unit path for a canonical CLT slug, or `null` when unknown. */
export function getCltSeedFootprintPath(slug: string): string | null {
  return PATHS.get(slug as CltNeighborhoodSlug) ?? null;
}
